class TestimonialsPerfume {
  constructor(section) {
    this.section = section;
    this.container = section.querySelector('.testimonials-container');
    this.grid = section.querySelector('.testimonials-grid');
    this.cards = section.querySelectorAll('.testimonial-card');
    this.prevBtn = section.querySelector('.nav-prev');
    this.nextBtn = section.querySelector('.nav-next');
    this.progressFill = section.querySelector('.progress-fill');
    
    // Get settings
    this.cardsPerView = parseInt(section.dataset.cardsPerView) || 3;
    
    // Initialize
    this.currentPage = 0;
    this.totalPages = Math.ceil(this.cards.length / this.cardsPerView);
    
    if (this.cards.length > this.cardsPerView) {
      this.init();
    }
  }
  
  init() {
    // Set up navigation
    this.setupNavigation();
    
    // Set up touch/swipe support
    this.setupTouch();
    
    // Set up keyboard navigation
    this.setupKeyboard();
    
    // Initialize view
    this.updateView();
    
    // Set up resize observer
    this.setupResize();
    
    // Animate fragrance notes on hover
    this.setupFragranceNotes();
    
    // Add entrance animations
    this.animateEntrance();
  }
  
  setupNavigation() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goToPage(this.currentPage - 1));
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goToPage(this.currentPage + 1));
    }
  }
  
  
  setupTouch() {
    let startX = 0;
    let endX = 0;
    
    this.grid.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });
    
    this.grid.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      this.handleSwipe(startX, endX);
    });
    
    // Mouse drag support
    let mouseDown = false;
    let mouseStartX = 0;
    
    this.grid.addEventListener('mousedown', (e) => {
      mouseDown = true;
      mouseStartX = e.clientX;
      this.grid.style.cursor = 'grabbing';
    });
    
    window.addEventListener('mouseup', (e) => {
      if (mouseDown) {
        mouseDown = false;
        this.grid.style.cursor = 'grab';
        this.handleSwipe(mouseStartX, e.clientX);
      }
    });
    
    window.addEventListener('mousemove', (e) => {
      if (mouseDown) {
        e.preventDefault();
      }
    });
    
    // Mouse wheel scrolling
    this.container.addEventListener('wheel', (e) => {
      e.preventDefault();
      
      // Determine scroll direction
      const delta = e.deltaY || e.deltaX;
      
      if (delta > 0) {
        // Scroll down/right - next page
        this.goToPage(this.currentPage + 1);
      } else if (delta < 0) {
        // Scroll up/left - previous page
        this.goToPage(this.currentPage - 1);
      }
    }, { passive: false });
  }
  
  handleSwipe(startX, endX) {
    const threshold = 50;
    const diff = startX - endX;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swipe left - next page
        this.goToPage(this.currentPage + 1);
      } else {
        // Swipe right - previous page
        this.goToPage(this.currentPage - 1);
      }
    }
  }
  
  setupKeyboard() {
    this.section.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.goToPage(this.currentPage - 1);
      } else if (e.key === 'ArrowRight') {
        this.goToPage(this.currentPage + 1);
      }
    });
    
    // Make section focusable
    this.section.setAttribute('tabindex', '0');
  }
  
  goToPage(page) {
    // Validate page number
    if (page < 0) page = 0;
    if (page >= this.totalPages) page = this.totalPages - 1;
    
    // Update current page
    this.currentPage = page;
    
    // Update view
    this.updateView();
    
    // Update progress line
    this.updateProgress();
    
    // Update button states
    this.updateButtons();
  }
  
  updateView() {
    // Get actual card width and gap
    const cardWidth = this.cards[0].offsetWidth;
    const gap = parseInt(window.getComputedStyle(this.grid).gap) || 30;
    
    // Calculate how many cards actually fit
    const containerWidth = this.container.offsetWidth;
    const actualCardsPerView = Math.floor(containerWidth / (cardWidth + gap));
    
    // Update cards per view based on actual width
    this.cardsPerView = Math.min(actualCardsPerView, parseInt(this.section.dataset.cardsPerView) || 3);
    
    // Recalculate total pages
    this.totalPages = Math.ceil(this.cards.length / this.cardsPerView);
    
    // Ensure current page is valid
    if (this.currentPage >= this.totalPages) {
      this.currentPage = this.totalPages - 1;
    }
    
    // Calculate transform value
    const translateX = -(this.currentPage * this.cardsPerView * (cardWidth + gap));
    
    // Apply smooth transition
    this.grid.style.transform = `translateX(${translateX}px)`;
    this.grid.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    
    // Update visibility for screen readers
    this.cards.forEach((card, index) => {
      const pageStart = this.currentPage * this.cardsPerView;
      const pageEnd = pageStart + this.cardsPerView;
      
      if (index >= pageStart && index < pageEnd) {
        card.setAttribute('aria-hidden', 'false');
        card.style.opacity = '1';
        card.style.pointerEvents = 'auto';
      } else {
        card.setAttribute('aria-hidden', 'true');
        card.style.opacity = '0.3';
        card.style.pointerEvents = 'none';
      }
    });
  }
  
  updateProgress() {
    if (this.progressFill) {
      // Calculate progress percentage
      const progressPercentage = ((this.currentPage + 1) / this.totalPages) * 100;
      this.progressFill.style.width = `${progressPercentage}%`;
    }
  }
  
  updateButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentPage === 0;
      this.prevBtn.setAttribute('aria-disabled', this.currentPage === 0);
    }
    
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentPage === this.totalPages - 1;
      this.nextBtn.setAttribute('aria-disabled', this.currentPage === this.totalPages - 1);
    }
  }
  
  setupResize() {
    const resizeObserver = new ResizeObserver(() => {
      // Recalculate on resize
      this.updateView();
    });
    
    resizeObserver.observe(this.container);
  }
  
  setupFragranceNotes() {
    const noteBadges = this.section.querySelectorAll('.note-badge');
    
    noteBadges.forEach(badge => {
      badge.addEventListener('mouseenter', function() {
        // Create ripple effect
        const ripple = document.createElement('span');
        ripple.className = 'note-ripple';
        ripple.style.cssText = `
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          border-radius: 20px;
          background: currentColor;
          opacity: 0.2;
          transform: translate(-50%, -50%) scale(0);
          animation: noteRipple 0.6s ease-out;
          pointer-events: none;
        `;
        
        // Add animation
        const style = document.createElement('style');
        style.textContent = `
          @keyframes noteRipple {
            to {
              transform: translate(-50%, -50%) scale(2);
              opacity: 0;
            }
          }
        `;
        
        if (!document.querySelector('#note-ripple-style')) {
          style.id = 'note-ripple-style';
          document.head.appendChild(style);
        }
        
        badge.style.position = 'relative';
        badge.appendChild(ripple);
        
        // Remove after animation
        setTimeout(() => ripple.remove(), 600);
      });
    });
  }
  
  animateEntrance() {
    // Use Intersection Observer for entrance animations
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Animate scent orbs
          const orbs = entry.target.querySelectorAll('.scent-orb');
          orbs.forEach((orb, index) => {
            setTimeout(() => {
              orb.style.animation = 'orbEntrance 0.8s ease-out forwards';
            }, index * 100);
          });
          
          // Animate stars
          const stars = entry.target.querySelectorAll('.star.filled');
          stars.forEach((star, index) => {
            setTimeout(() => {
              star.style.animation = 'starPop 0.6s ease-out';
            }, 200 + (index * 50));
          });
          
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    // Observe each card
    this.cards.forEach(card => {
      observer.observe(card);
    });
    
    // Add entrance animation keyframes
    if (!document.querySelector('#testimonial-entrance-styles')) {
      const style = document.createElement('style');
      style.id = 'testimonial-entrance-styles';
      style.textContent = `
        @keyframes orbEntrance {
          from {
            transform: scale(0) rotate(-180deg);
            opacity: 0;
          }
          to {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }
}

// Initialize all testimonial sections
document.addEventListener('DOMContentLoaded', () => {
  const testimonialSections = document.querySelectorAll('.testimonials-section');
  
  testimonialSections.forEach(section => {
    // Set cards per view from settings
    const settingsScript = section.querySelector('script[type="application/json"]');
    if (settingsScript) {
      try {
        const settings = JSON.parse(settingsScript.textContent);
        section.dataset.cardsPerView = settings.cards_per_view || 3;
      } catch (e) {
        console.error('Error parsing testimonial settings:', e);
      }
    }
    
    new TestimonialsPerfume(section);
  });
});

// Re-initialize on Shopify section events
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.testimonials-section');
  if (section) {
    new TestimonialsPerfume(section);
  }
});